import React, { useEffect, useState } from "react";
import { MdClose } from "react-icons/md";
import { isAdOptOut, readPrefs, savePrefs } from "../../utils/privacyConsent";

// "Privacy Choices" in Settings > Legal (client, ticket 13083): where a visitor
// changes the consent banner's answer later. It holds the banner's two optional
// categories with the banner's own descriptions (note 15227 final copy), on
// every surface, the app's WebView included.
//
// Do Not Sell or Share is a separate control with its own modal and is not
// changed here. It does turn advertising off on top of this choice, though,
// so while it (or a Global Privacy Control signal) is on, the Advertising
// switch shows that effective "off", cannot be switched on here, and says
// why (client, dev finding 2026-10-16).
//
// Each switch saves at once through savePrefs(), the same path as the banner,
// so Consent Mode, the backend consent ledger (consent_sync) and the native
// shell (consent_changed) all get the change. Both categories are saved
// together, so a first save in a prior-consent region is a complete answer
// and the banner does not come back afterwards.

// The switch positions for the stored choice: in the EEA, the UK and
// Switzerland both are off until granted; elsewhere both are on until turned
// off (the opt-out model).
const currentChoices = () => {
  const P = typeof window !== "undefined" ? window.QushiPrivacy : null;
  const prefs = readPrefs() || {};
  let strict = false;
  try {
    strict = !!P && P.isPriorConsentRegion();
  } catch (e) { }
  const choice = (value) => (strict ? value === true : value !== false);
  return {
    analyticsConsent: choice(prefs.analyticsConsent),
    adsConsent: choice(prefs.adsConsent),
  };
};

// Mobile never says "cookies", and the app says "app" where the browser says
// "site" (note 15227).
const placeWord = () =>
  window.QushiSurface && window.QushiSurface.surface !== "browser" ? "app" : "site";

const Switch = ({ on, onToggle, label, disabled = false }) => (
  <div
    onClick={disabled ? undefined : onToggle}
    role="switch"
    aria-checked={on}
    aria-label={label}
    aria-disabled={disabled ? "true" : undefined}
    tabIndex={disabled ? -1 : 0}
    onKeyDown={(e) => {
      if (disabled) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onToggle();
      }
    }}
    className={`flex h-7 w-14 shrink-0 items-center rounded-full transition-all ${
      disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
    }`}
    style={{
      backgroundColor: on ? "#35b23c" : "#d3d3d3",
      justifyContent: on ? "flex-end" : "flex-start",
      padding: "3px",
    }}
  >
    <div className="h-6 w-6 rounded-full bg-white shadow" />
  </div>
);

const PrivacyChoicesModal = ({ isOpen, onClose }) => {
  const [choices, setChoices] = useState(currentChoices);

  // Re-read on every open: the choice may have changed elsewhere meanwhile
  // (the banner, or Do Not Sell or Share).
  useEffect(() => {
    if (isOpen) setChoices(currentChoices());
  }, [isOpen]);

  if (!isOpen) return null;

  const adOptOut = isAdOptOut();

  const toggle = (key) => {
    setChoices((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      savePrefs(next);
      return next;
    });
  };

  const rows = [
    {
      key: "analyticsConsent",
      label: "Analytics",
      description: "How people use QUSHi, so we can improve it.",
    },
    {
      key: "adsConsent",
      label: "Advertising",
      description: "Measuring our ads and showing you more relevant ones.",
      locked: adOptOut,
      note: "Off while Do Not Sell or Share is on.",
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
        >
          <MdClose size={22} />
        </button>

        <h2 className="pr-8 text-title-subpage font-bold text-[#004d4d]">
          Privacy Choices
        </h2>

        <div className="mt-6 space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-medium text-gray-800">Essential</p>
              <p className="mt-0.5 text-sm text-secondary">
                Required for the {placeWord()} to work. (Always on)
              </p>
            </div>
          </div>
          {rows.map((row) => (
            <div key={row.key} className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium text-gray-800">{row.label}</p>
                <p className="mt-0.5 text-sm text-secondary">{row.description}</p>
                {row.locked && (
                  <p className="mt-1 text-sm text-secondary">{row.note}</p>
                )}
              </div>
              <Switch
                on={row.locked ? false : choices[row.key]}
                onToggle={() => toggle(row.key)}
                label={row.label}
                disabled={row.locked}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PrivacyChoicesModal;
